import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader } from "../../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { ExportMenu } from "../../ui/export-menu";
import { SummaryHeader } from "../../ui/SummaryHeader";
import MarketingLoader from "../../../components/loaders/MarketingLoader";
import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMarketingWholesalePromo } from "../hooks/useMarketingWholesalePromo";
import { formatCurrency } from "../../../utils/formatCurrency";

type MarketingPromoProps = {
  month?: string;
  limit?: number;
};

type Promo = {
  marketing_person: string;
  date: string;
  merch_cost: number;
  tax_cost: number;
  freight_cost: number;
  total_cost: number;
};

export function WholesaleMarketingPromo({
  month = "2025-04",
  limit = 40,
}: MarketingPromoProps) {
  const { data, isLoading } = useMarketingWholesalePromo(month, limit);
  const promos = data ?? [];

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [personFilter, setPersonFilter] = useState<string>("");

  const filteredPromos = useMemo(() => {
    if (!personFilter) return promos;
    return promos.filter((promo: Promo) =>
      promo.marketing_person.toLowerCase().includes(personFilter.toLowerCase())
    );
  }, [promos, personFilter]);

  const columns: ColumnDef<Promo>[] = [
    {
      accessorKey: "marketing_person",
      header: "Promo",
      cell: ({ row }) => row.getValue("marketing_person"),
    },
    {
      accessorKey: "date",
      header: "Date",
      cell: ({ row }) => row.getValue("date"),
    },
    {
      accessorKey: "merch_cost",
      header: "Merchandise Cost",
      cell: ({ row }) => formatCurrency(row.getValue("merch_cost")),
    },
    {
      accessorKey: "tax_cost",
      header: "Tax Cost",
      cell: ({ row }) => formatCurrency(row.getValue("tax_cost")),
    },
    {
      accessorKey: "freight_cost",
      header: "Freight Cost",
      cell: ({ row }) => formatCurrency(row.getValue("freight_cost")),
    },
    {
      accessorKey: "total_cost",
      header: "Total Cost",
      cell: ({ row }) =>
        formatCurrency(row.getValue("total_cost")),
    },
  ];

  const table = useReactTable({
    data: filteredPromos,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <>
      <SummaryHeader title="Wholesale Promo" />
      <Card className="col-span-full">
        <CardHeader>
          <ExportMenu
            data={table.getFilteredRowModel().rows.map((row) => row.original)}
            filename="wholesale-promo"
            buttonVariant="outline"
            buttonSize="sm"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="ml-auto">
                Columns <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {column.id}
                  </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
              <div className="flex-1">
                <Input
                  placeholder="Search Promo..."
                  value={personFilter}
                  onChange={(event) => setPersonFilter(event.target.value)}
                  className="max-w-sm"
                />
              </div>
            </div>
            <div className="rounded-md border">
              {isLoading ? (
                <MarketingLoader variant="product-tbl" itemCount={1} />
              ) : (
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow
                          key={row.id}
                          data-state={row.getIsSelected() && "selected"}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center"
                        >
                          No results.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </div>
            <div className="flex items-center justify-end space-x-2 py-4">
              <div className="flex-1 text-sm text-muted-foreground">
                {table.getFilteredRowModel().rows.length} of{" "}
                {table.getCoreRowModel().rows.length} row(s) displayed.
              </div>
              <div className="space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
